import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { can, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import './reminders.css';
import { reminderMeta, REMINDERS_RESOURCE, remindersKey, warsawToday, type Reminder } from './reminders';
import { useUnsavedGuard } from '../ui/unsaved';

/**
 * "Przypomnienia" in the event card's details (P4, owner remark 3/21): the texts of reminder rules whose trigger
 * is on the packlist and reminders added by hand; each can be ticked off, own ones deleted. Not ticked off on its
 * day, a reminder goes to the bell of the event's manager (server job).
 */
export function RemindersSection({ eventId }: { eventId: string }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [dueDay, setDueDay] = useState('');
  // ticks shown before the server answers
  const [ticking, setTicking] = useState<Record<string, boolean>>({});
  const mayView = !!me && can(me, REMINDERS_RESOURCE, 'view');
  const list = useQuery({ queryKey: remindersKey(eventId), enabled: mayView, queryFn: () => api<{ reminders: Reminder[] }>(`/api/packlists/${eventId}/reminders`) });
  const refresh = () => queryClient.invalidateQueries({ queryKey: remindersKey(eventId) });

  const add = useMutation({
    mutationFn: (body: Record<string, unknown>) => api<Reminder>(`/api/packlists/${eventId}/reminders`, { method: 'POST', body }),
    onSuccess: async () => {
      setText('');
      setDueDay('');
      setAdding(false);
      await refresh();
    },
  });
  const tick = useMutation({
    mutationFn: (r: Reminder) => api<Reminder>(`/api/packlists/reminders/${r.id}`, { method: 'PATCH', body: { version: r.version, done: !r.done } }),
    onSettled: async (_data, _error, r) => {
      await refresh();
      setTicking(({ [r.id]: _, ...rest }) => rest);
    },
  });
  const remove = useMutation({
    mutationFn: (r: Reminder) => api<void>(`/api/packlists/reminders/${r.id}?version=${r.version}`, { method: 'DELETE' }),
    onSettled: refresh,
  });

  // owner 2026-10-23 21:14: typed and not saved: leaving asks first
  const addRef = useUnsavedGuard({
    dirty: adding && text.trim() !== '',
    save: async () => {
      await add.mutateAsync({ text: text.trim(), ...(me && fieldVisible(me, REMINDERS_RESOURCE, 'dueDay') && dueDay ? { dueDay } : {}) });
      return true;
    },
  });
  if (!me || !mayView) return null;
  const showDay = fieldVisible(me, REMINDERS_RESOURCE, 'dueDay');
  const mayTick = can(me, REMINDERS_RESOURCE, 'edit') && fieldVisible(me, REMINDERS_RESOURCE, 'done');
  const mayAdd = can(me, REMINDERS_RESOURCE, 'create') && fieldVisible(me, REMINDERS_RESOURCE, 'text');
  const mayDelete = can(me, REMINDERS_RESOURCE, 'delete');
  const items = list.data?.reminders?.map((r) => (r.id in ticking ? { ...r, done: ticking[r.id]! } : r));
  const open = items?.filter((r) => !r.done).length ?? 0;
  const today = warsawToday();
  const writeError = [tick.error, remove.error].find((e) => e instanceof ApiError) as ApiError | undefined;
  const addError = add.error instanceof ApiError ? add.error : null;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (text.trim() === '') return;
    add.mutate({ text: text.trim(), ...(showDay && dueDay ? { dueDay } : {}) });
  }

  return (
    <section className="event-reminders" aria-label="Przypomnienia" data-feature-id="packlists.reminders">
      <div className="event-reminders-head">
        <h3>
          Przypomnienia
          {open > 0 && <span className="count" aria-label={`do zrobienia: ${open}`}>{open}</span>}
        </h3>
        {mayAdd && !adding && (
          <button className="btn small ghost" type="button" onClick={() => setAdding(true)}>
            + Dodaj przypomnienie
          </button>
        )}
      </div>
      {list.isLoading && <p className="muted small" role="status">Wczytywanie…</p>}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać przypomnień'}</p>}
      {writeError?.status === 409 && (
        <ConflictNotice
          onReload={() => {
            tick.reset();
            remove.reset();
            void refresh();
          }}
        />
      )}
      {writeError && writeError.status !== 409 && <p className="alert" role="alert">{writeError.message}</p>}
      {items && items.length === 0 && !adding && <p className="muted small">Brak przypomnień. Pojawią się tu same z reguł „przypomnij” (np. przy konkretnym mikserze), możesz też dodać ręczne.</p>}
      {items && items.length > 0 && (
        <ul className="event-reminders-list">
          {items.map((r) => (
            <li key={r.id} className={r.done ? 'done' : undefined}>
              <label className="check">
                <input type="checkbox" checked={!!r.done} disabled={!mayTick || tick.isPending} onChange={() => {
                  setTicking((t) => ({ ...t, [r.id]: !r.done }));
                  tick.mutate(r);
                }} aria-label={`Zrobione: ${r.text ?? 'przypomnienie'}`} />
                <span>
                  <span className="event-reminder-text">{r.text}</span>
                  <span className="event-reminder-meta">{reminderMeta(showDay ? r : { ...r, dueDay: null }, today)}</span>
                </span>
              </label>
              {r.source === 'manual' && mayDelete && (
                <button className="btn small ghost" type="button" aria-label={`Usuń przypomnienie: ${r.text ?? ''}`} disabled={remove.isPending} onClick={() => remove.mutate(r)}>
                  Usuń
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
      {adding && (
        <form ref={addRef} className="event-reminders-add" aria-label="Nowe przypomnienie" onSubmit={submit}>
          <label className="field">
            <span>Treść</span>
            <input type="text" value={text} onChange={(e) => setText(e.target.value)} placeholder="np. Zadzwonić do organizatora w sprawie prądu" autoComplete="off" autoFocus maxLength={1000} />
          </label>
          {showDay && (
            <label className="field day">
              <span>Termin (opcjonalnie)</span>
              <input type="date" value={dueDay} onChange={(e) => setDueDay(e.target.value)} />
            </label>
          )}
          <div className="actions">
            <button className="btn small primary" type="submit" disabled={add.isPending || text.trim() === ''}>
              Dodaj
            </button>
            <button className="btn small" type="button" onClick={() => { setAdding(false); add.reset(); }}>
              Anuluj
            </button>
          </div>
          {addError && (
            <p className="alert" role="alert">
              {fieldIssues(addError).map((i) => i.message).join(' ') || addError.message}
            </p>
          )}
          {showDay && <p className="muted small">W dniu terminu o 9:00, jeśli nie będzie odhaczone, przypomnienie trafi do dzwonka Twojego i opiekuna wydarzenia.</p>}
        </form>
      )}
    </section>
  );
}
